/**
 * Surface Capability Registry (Agentic UI Fabric P0, design doc §4.2).
 *
 * The self-describing UI: every addressable element opts in with
 * `data-guide-id` via useGuideRegister, which registers a RegistryEntry here
 * for exactly its mounted lifetime. The registry is therefore COLLECTED from
 * the live UI, never hand-maintained — and it IS the ACL filter for the
 * ui_context manifest: components only mount (and config-driven navigate
 * targets only register) what the user's role/ACL already lets them see.
 */
import { create } from 'zustand'

import i18n from '@/i18n'
import { createLogger } from '@/utils/logger'

const logger = createLogger('fabricRegistry')

export type GuideKind = 'navigate' | 'control' | 'field' | 'commit' | 'widget'

export interface RegistryEntry {
  /** Stable, i18n-independent id, e.g. "patient.admit.submit". */
  guideId: string
  /** Surface the entry lives on: "home" | "transcript" | "<subdomain>". */
  surfaceId: string
  kind: GuideKind
  /** i18n key for coach-marks / the ui_context label (a literal falls through t()). */
  labelKey: string
}

interface RegisteredEntry extends RegistryEntry {
  /** Host-side activation for kind:"navigate" entries (never sent on the wire). */
  activate?: () => void
}

/** Wire shape attached to the turn POST — mirrors Go chat.UIContext (snake_case). */
export interface UiContextWire {
  surface_id: string
  entries: { guide_id: string; kind: GuideKind; label: string }[]
}

interface FabricRegistryState {
  surfaceId: string
  entries: Map<string, RegisteredEntry>
  /**
   * Ref-counted session ids this tab is actively engaged with — a mounted
   * session surface (chat pane, board chat side-channel). The count (not a
   * Set) is load-bearing: two components of the same surface may engage the
   * same session concurrently (e.g. board + its side-channel), and the first
   * unmount must not release the other's claim.
   */
  engagedSessions: Map<string, number>
  setSurface: (surfaceId: string) => void
  /** Claim engagement with a session for a component's lifetime; returns release. */
  engageSession: (sessionId: string) => () => void
  isSessionEngaged: (sessionId: string) => boolean
  register: (entry: RegistryEntry, activate?: () => void) => () => void
  get: (guideId: string) => RegisteredEntry | undefined
  uiContext: () => UiContextWire
}

export const useFabricRegistryStore = create<FabricRegistryState>((set, get) => ({
  surfaceId: 'transcript',
  entries: new Map(),
  engagedSessions: new Map(),
  setSurface: (surfaceId) => set({ surfaceId }),
  engageSession: (sessionId) => {
    set((s) => {
      const next = new Map(s.engagedSessions)
      next.set(sessionId, (next.get(sessionId) ?? 0) + 1)
      return { engagedSessions: next }
    })
    return () => {
      set((s) => {
        const next = new Map(s.engagedSessions)
        const count = (next.get(sessionId) ?? 0) - 1
        if (count > 0) next.set(sessionId, count)
        else next.delete(sessionId)
        return { engagedSessions: next }
      })
    }
  },
  isSessionEngaged: (sessionId) => (get().engagedSessions.get(sessionId) ?? 0) > 0,
  register: (entry, activate) => {
    // Co-pilot ceiling (structural): ONLY a `navigate` entry may carry an
    // activation callback. A control/commit/field/widget is the nurse's own
    // tap and must never be agent-activatable, so an activate() wrongly passed
    // for a non-navigate kind is dropped here — the registry can never hold an
    // activatable commit, regardless of caller mistakes.
    let safeActivate = activate
    if (activate && entry.kind !== 'navigate') {
      logger.warn('activate_dropped_non_navigate', { guideId: entry.guideId, kind: entry.kind })
      safeActivate = undefined
    }
    set((s) => {
      const next = new Map(s.entries)
      next.set(entry.guideId, { ...entry, activate: safeActivate })
      return { entries: next }
    })
    return () => {
      set((s) => {
        const next = new Map(s.entries)
        next.delete(entry.guideId)
        return { entries: next }
      })
    }
  },
  get: (guideId) => get().entries.get(guideId),
  // IDs + labels only — never DOM, never clinical data. Labels are resolved
  // through i18n at attach time so the agent reads meaning, not keys (a
  // non-key labelKey passes through t() verbatim).
  uiContext: () => ({
    surface_id: get().surfaceId,
    entries: [...get().entries.values()].map((e) => ({
      guide_id: e.guideId,
      kind: e.kind,
      label: i18n.t(e.labelKey),
    })),
  }),
}))
